import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  activeMenu = 'services';
  isMobileMenuOpen = false;
  isScrolled = false;
  private routeSubscription?: Subscription;

  constructor(private router: Router, private viewportScroller: ViewportScroller) {}

  ngOnInit(): void {
    this.updateActiveMenu(this.router.url);
    this.onWindowScroll();

    this.routeSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navEvent = event as NavigationEnd;
        this.updateActiveMenu(navEvent.urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  isMenuActive(menuKey: string): boolean {
    return this.activeMenu === menuKey;
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  onMenuItemClick(): void {
    this.isMobileMenuOpen = false;
  }

  navigateToSection(sectionId: 'services' | 'location', event: Event): void {
    event.preventDefault();
    this.onMenuItemClick();

    const parsed = this.router.parseUrl(this.router.url);
    const primaryPath = parsed.root.children.primary
      ? parsed.root.children.primary.segments.map((segment) => segment.path).join('/')
      : '';

    if (primaryPath && primaryPath !== '') {
      this.router.navigate(['/']).then(() => {
        setTimeout(() => {
          this.scrollToSectionWithHeaderOffset(sectionId);
          this.activeMenu = sectionId;
        }, 60);
      });
      return;
    }

    this.scrollToSectionWithHeaderOffset(sectionId);
    this.activeMenu = sectionId;
  }

  private scrollToSectionWithHeaderOffset(sectionId: string): void {
    const target = document.getElementById(sectionId);
    if (!target) {
      this.viewportScroller.scrollToAnchor(sectionId);
      return;
    }

    const header = document.querySelector('.app-header') as HTMLElement | null;
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const additionalGap = 8;
    const top = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - additionalGap;

    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.isScrolled = window.scrollY > 4;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (window.innerWidth > 576) {
      this.isMobileMenuOpen = false;
    }
  }

  private updateActiveMenu(url: string): void {
    const parsed = this.router.parseUrl(url);
    const primaryPath = parsed.root.children.primary
      ? parsed.root.children.primary.segments.map((segment) => segment.path).join('/')
      : '';
    const fragment = parsed.fragment || '';

    if (primaryPath === 'pricing') {
      this.activeMenu = 'pricing';
      return;
    }

    if (primaryPath === 'contact') {
      this.activeMenu = 'contact';
      return;
    }

    if (fragment === 'location' || fragment === 'services') {
      this.activeMenu = fragment;
      return;
    }

    this.activeMenu = 'services';
  }
}
